import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/Input';
import {
  metaAdsApi,
  type MetaCampaignObjective,
  type MetaCustomEventType,
  type MetaPromotedObject,
} from '@/services/api/metaAds';

interface AdSetPromotedObjectStepProps {
  parentObjective: MetaCampaignObjective;
  adAccountId: string;
  /** From `meta_ads_credentials.page_id`. Null = no default page picked
   *  at link time; the LEADS path needs to show a page picker first. */
  storedPageId: string | null;
  promotedObject: MetaPromotedObject;
  onChange: (po: MetaPromotedObject) => void;
  errors?: {
    pixel?: string;
    customEventType?: string;
    page?: string;
    applicationId?: string;
    objectStoreUrl?: string;
  };
}

const CUSTOM_EVENT_LABELS: Record<MetaCustomEventType, string> = {
  PURCHASE: 'Purchase',
  LEAD: 'Lead',
  COMPLETE_REGISTRATION: 'Complete registration',
  ADD_TO_CART: 'Add to cart',
  ADD_TO_WISHLIST: 'Add to wishlist',
  ADD_PAYMENT_INFO: 'Add payment info',
  CONTENT_VIEW: 'Content view',
  INITIATE_CHECKOUT: 'Initiate checkout',
  SEARCH: 'Search',
  SUBSCRIBE: 'Subscribe',
  START_TRIAL: 'Start trial',
  SUBMIT_APPLICATION: 'Submit application',
  OTHER: 'Other',
};

/**
 * Conditional step shown only for OUTCOME_SALES / OUTCOME_LEADS /
 * OUTCOME_APP_PROMOTION parent objectives. Wizard's step-nav skips this
 * step otherwise.
 *
 * Per-objective sub-form:
 *  - SALES: pixel dropdown (React Query → listPixels) + custom_event_type
 *  - LEADS: page picker as radio cards. Each card shows the Page's lead
 *    forms inline (via `listLeadForms`) so the user sees which Pages
 *    have forms ready before selecting. The form itself is NOT selected
 *    here — Meta's `promoted_object` for LEADS only takes `page_id`; the
 *    form_id belongs on the ad creative's `call_to_action.value.lead_gen_form_id`
 *    and is selected in the ad creative wizard. See task #17.
 *  - APP_PROMOTION: application_id text + object_store_url URL
 */
export const AdSetPromotedObjectStep: React.FC<AdSetPromotedObjectStepProps> = ({
  parentObjective,
  adAccountId,
  storedPageId,
  promotedObject,
  onChange,
  errors,
}) => {
  const set = (patch: Partial<MetaPromotedObject>) =>
    onChange({ ...promotedObject, ...patch });

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Promoted object
        </h3>
        <p className="text-sm text-gray-600">
          Meta needs to know what Sale / Lead / App install this ad set is
          optimizing for.
        </p>
      </div>

      {parentObjective === 'OUTCOME_SALES' && (
        <SalesSubForm
          adAccountId={adAccountId}
          pixelId={promotedObject.pixel_id}
          customEventType={promotedObject.custom_event_type}
          onChangePixel={(pixel_id) => set({ pixel_id })}
          onChangeEvent={(custom_event_type) => set({ custom_event_type })}
          errors={errors}
        />
      )}

      {parentObjective === 'OUTCOME_LEADS' && (
        <LeadsSubForm
          adAccountId={adAccountId}
          storedPageId={storedPageId}
          pageId={promotedObject.page_id}
          onChangePage={(page_id) => set({ page_id })}
          errors={errors}
        />
      )}

      {parentObjective === 'OUTCOME_APP_PROMOTION' && (
        <AppPromotionSubForm
          applicationId={promotedObject.application_id}
          objectStoreUrl={promotedObject.object_store_url}
          onChangeApp={(application_id) => set({ application_id })}
          onChangeUrl={(object_store_url) => set({ object_store_url })}
          errors={errors}
        />
      )}
    </div>
  );
};

// ── SALES ──────────────────────────────────────────────────────────────────

interface SalesSubFormProps {
  adAccountId: string;
  pixelId?: string;
  customEventType?: MetaCustomEventType;
  onChangePixel: (id: string) => void;
  onChangeEvent: (event: MetaCustomEventType) => void;
  errors?: { pixel?: string; customEventType?: string };
}

const SalesSubForm: React.FC<SalesSubFormProps> = ({
  adAccountId,
  pixelId,
  customEventType,
  onChangePixel,
  onChangeEvent,
  errors,
}) => {
  const { data: pixels = [], isLoading } = useQuery({
    queryKey: ['meta-pixels', adAccountId],
    queryFn: () => metaAdsApi.listPixels(adAccountId),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="space-y-4">
      <div>
        <label
          htmlFor="meta-pixel-select"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Pixel <span className="text-red-500" aria-label="required">*</span>
        </label>
        {isLoading ? (
          <p className="text-xs text-gray-500">Loading pixels…</p>
        ) : pixels.length === 0 ? (
          <p className="text-xs text-yellow-600">
            No pixels found on this ad account. Set one up in Meta Ads Manager
            before creating a conversions ad set.
          </p>
        ) : (
          <select
            id="meta-pixel-select"
            value={pixelId ?? ''}
            onChange={(e) => onChangePixel(e.target.value)}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-invalid={Boolean(errors?.pixel)}
            required
          >
            <option value="">Pick a pixel…</option>
            {pixels.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || p.id}
              </option>
            ))}
          </select>
        )}
        {errors?.pixel && (
          <p role="alert" className="text-sm text-red-600 mt-1">
            {errors.pixel}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="meta-custom-event-type"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Conversion event{' '}
          <span className="text-red-500" aria-label="required">*</span>
        </label>
        <select
          id="meta-custom-event-type"
          value={customEventType ?? ''}
          onChange={(e) =>
            onChangeEvent(e.target.value as MetaCustomEventType)
          }
          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-invalid={Boolean(errors?.customEventType)}
          required
        >
          <option value="">Pick a conversion event…</option>
          {(Object.keys(CUSTOM_EVENT_LABELS) as MetaCustomEventType[]).map(
            (e) => (
              <option key={e} value={e}>
                {CUSTOM_EVENT_LABELS[e]}
              </option>
            ),
          )}
        </select>
        {errors?.customEventType && (
          <p role="alert" className="text-sm text-red-600 mt-1">
            {errors.customEventType}
          </p>
        )}
      </div>
    </div>
  );
};

// ── LEADS ──────────────────────────────────────────────────────────────────

interface LeadsSubFormProps {
  adAccountId: string;
  storedPageId: string | null;
  pageId?: string;
  onChangePage: (id: string) => void;
  errors?: { page?: string };
}

const LeadsSubForm: React.FC<LeadsSubFormProps> = ({
  adAccountId,
  storedPageId,
  pageId,
  onChangePage,
  errors,
}) => {
  // Normalize a stored blank string to null up front. Without this,
  // `storedPageId === ''` would skip the picker even when there's no
  // real default to fall back to.
  const normalizedStoredPageId =
    storedPageId && storedPageId.length > 0 ? storedPageId : null;
  const effectivePageId = pageId ?? normalizedStoredPageId ?? '';
  const needPagePicker = normalizedStoredPageId === null;

  const { data: pages = [], isLoading: pagesLoading } = useQuery({
    queryKey: ['meta-pages', adAccountId],
    queryFn: () => metaAdsApi.listPages(adAccountId),
    staleTime: 5 * 60 * 1000,
    enabled: needPagePicker,
  });

  // If there's a stored default Page, render only that one card (read-
  // only). The user previously committed to this Page at link time;
  // don't make them re-pick — but still surface form names so they can
  // see what's available before continuing.
  if (!needPagePicker) {
    return (
      <div className="space-y-2">
        <PageCard
          page={{ id: normalizedStoredPageId!, name: undefined }}
          adAccountId={adAccountId}
          selected
          readOnly
        />
        {errors?.page && (
          <p role="alert" className="text-sm text-red-600 mt-1">
            {errors.page}
          </p>
        )}
      </div>
    );
  }

  return (
    <fieldset>
      <legend className="block text-sm font-medium text-gray-700 mb-2">
        Facebook Page{' '}
        <span className="text-red-500" aria-label="required">*</span>
      </legend>
      <p className="text-xs text-gray-500 mb-2">
        Pick the Page running this lead-ads campaign. The lead form
        itself is picked later, when you create the ad creative.
      </p>
      {pagesLoading ? (
        <p className="text-xs text-gray-500">Loading pages…</p>
      ) : pages.length === 0 ? (
        <p className="text-xs text-yellow-600">
          No pages found. You need a Facebook Page to run lead ads.
        </p>
      ) : (
        <div className="space-y-2">
          {pages.map((p) => (
            <PageCard
              key={p.id}
              page={p}
              adAccountId={adAccountId}
              selected={effectivePageId === p.id}
              onSelect={() => onChangePage(p.id)}
            />
          ))}
        </div>
      )}
      {errors?.page && (
        <p role="alert" className="text-sm text-red-600 mt-2">
          {errors.page}
        </p>
      )}
    </fieldset>
  );
};

interface PageCardProps {
  page: { id: string; name?: string };
  adAccountId: string;
  selected: boolean;
  /** Render as a non-interactive informational card. Used when the
   *  user has a stored default Page from link time and we don't want
   *  to surface other options. */
  readOnly?: boolean;
  onSelect?: () => void;
}

const PageCard: React.FC<PageCardProps> = ({
  page,
  adAccountId,
  selected,
  readOnly,
  onSelect,
}) => {
  // Per-card lead-form query. For users with many Pages this fan-out
  // can be heavy; see `docs/META_LEAD_ADS_WIZARD_FOLLOWUPS.md` §3 for the
  // deferred-loading plan. For the current test member (≤5 Pages)
  // this is fine, and React Query's 5-min staleTime keeps repeat
  // renders cache-hit.
  const {
    data: leadForms = [],
    isLoading: formsLoading,
    isError: formsErrored,
  } = useQuery({
    queryKey: ['meta-lead-forms', page.id, adAccountId],
    queryFn: () => metaAdsApi.listLeadForms(page.id, adAccountId),
    staleTime: 5 * 60 * 1000,
  });

  let formsDisplay: string;
  let formsClassName = '';
  if (formsLoading) {
    formsDisplay = 'Loading…';
  } else if (formsErrored) {
    // Distinguish "couldn't load" from the legitimate "no forms exist"
    // case below — otherwise a transient 5xx or a scope/permission gap
    // renders as "None", and the user has no signal to retry or
    // re-link.
    formsDisplay = "Couldn't load forms — please try again.";
    formsClassName = 'text-amber-700';
  } else if (leadForms.length === 0) {
    formsDisplay = 'None';
    formsClassName = 'text-gray-400 italic';
  } else {
    formsDisplay = leadForms.map((f) => f.name || f.id).join(', ');
  }

  const innerContent = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-gray-900">
          {page.name || page.id}
        </p>
        <p className="text-xs text-gray-500 font-mono">{page.id}</p>
      </div>
      <p className="mt-1 text-xs text-gray-600">
        <span className="font-medium">Lead forms on this Page:</span>{' '}
        <span className={formsClassName} role={formsErrored ? 'alert' : undefined}>
          {formsDisplay}
        </span>
      </p>
    </>
  );

  if (readOnly) {
    return (
      <div className="rounded-md border border-gray-300 bg-gray-50 px-4 py-3">
        {innerContent}
      </div>
    );
  }

  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition-colors ${
        selected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-300 hover:bg-gray-50'
      }`}
    >
      <input
        type="radio"
        name="meta-page-card"
        value={page.id}
        checked={selected}
        onChange={onSelect}
        className="mt-1 flex-shrink-0"
        aria-label={`Select ${page.name || page.id}`}
      />
      <div className="flex-1 min-w-0">{innerContent}</div>
    </label>
  );
};

// ── APP_PROMOTION ──────────────────────────────────────────────────────────

interface AppPromotionSubFormProps {
  applicationId?: string;
  objectStoreUrl?: string;
  onChangeApp: (id: string) => void;
  onChangeUrl: (url: string) => void;
  errors?: { applicationId?: string; objectStoreUrl?: string };
}

const AppPromotionSubForm: React.FC<AppPromotionSubFormProps> = ({
  applicationId,
  objectStoreUrl,
  onChangeApp,
  onChangeUrl,
  errors,
}) => {
  const urlInvalid =
    objectStoreUrl !== undefined && objectStoreUrl !== ''
      ? !/^https?:\/\//.test(objectStoreUrl)
      : false;

  return (
    <div className="space-y-4">
      <div>
        <label
          htmlFor="meta-application-id"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          App ID <span className="text-red-500" aria-label="required">*</span>
        </label>
        <Input
          id="meta-application-id"
          type="text"
          inputMode="numeric"
          pattern="[0-9]+"
          value={applicationId ?? ''}
          onChange={(e) => onChangeApp(e.target.value.replace(/\D/g, ''))}
          placeholder="Meta app ID"
          aria-invalid={Boolean(errors?.applicationId)}
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          The Meta-side numeric app ID. Find it in your Meta Developer
          dashboard.
        </p>
        {errors?.applicationId && (
          <p role="alert" className="text-sm text-red-600 mt-1">
            {errors.applicationId}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="meta-object-store-url"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          App Store URL{' '}
          <span className="text-red-500" aria-label="required">*</span>
        </label>
        <Input
          id="meta-object-store-url"
          type="url"
          value={objectStoreUrl ?? ''}
          onChange={(e) => onChangeUrl(e.target.value)}
          placeholder="https://apps.apple.com/app/example/id1234567890"
          aria-invalid={Boolean(errors?.objectStoreUrl) || urlInvalid}
          required
        />
        {(errors?.objectStoreUrl || urlInvalid) && (
          <p role="alert" className="text-sm text-red-600 mt-1">
            {errors?.objectStoreUrl ??
              'URL must start with http:// or https://'}
          </p>
        )}
      </div>
    </div>
  );
};
